import Select from 'react-select';
import i18next from 'i18next';
import RequiredFormLabel from '../RequiredFormLabel/RequiredFormLabel';
import useLeadsStore from '../../store/LeadsStore';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import TiassistoElement from '../TiAssisto/TiassistoElement';
import ValidationError from '../ValidationError/ValidationError';
import { Col, Button, Form, FormControl, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { renderFloatAsStr, strToFloat } from '../../utils';
import { FaArrowRightLong, FaArrowLeftLong } from 'react-icons/fa6';
import { RoleEnum } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { LeadsWizardActiveStepEnum } from '../../api/models/Enums';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactSelectOption, RemoteSelectOption } from 'src/types';
import { toast } from 'react-toastify';
import { useApiClient } from '../../hooks/useApiClient';
import _ from 'lodash';
import { FaInfoCircle } from 'react-icons/fa';
import ToolTip from '../Tooltip/Tooltip';
import { useOpportunityUserFields } from '../../hooks/useOpportunityUserFields';
import { UserDTO } from '../../api';
import { getOpportunityCreationValidationSchema, ForceCommissionsZeroOnSegnalazione, ForceLeadSourceOnSegnalazione } from '../Opportunities/OpportunityCreation/OpportunityCreation';
import { applySegnalatoreCrossUpdate } from '../../utils/opportunity';

const SfOpportunityForm = ({ opportunityFields }) => {
  const { setLeadWizardData, leadWizardData, setWizardActiveStep, activeLead } = useLeadsStore();
  const { t } = useTranslation();
  const { userRole, userReferencePartnerList, user, userIsSalesforceSales } = useAuth();
  const { allSalesUsersDTO, allSegnalatori, isBam, isMasterOrBackoffice } = useOpportunityUserFields(userRole);

  const commissionsLabel = isMasterOrBackoffice
    ? t('provvigioni_canale')
    : userRole === RoleEnum.AREA_MANAGER
      ? t('provvigioni_dealer_euro')
      : t('commissions');

  const { apiClient, handleApiResponse } = useApiClient()
  const [salesUsers, setSalesUsers] = useState([]);
  const [referenceUsers, setReferenceUsers] = useState<RemoteSelectOption[]>([]);
  const [backupUsers, setBackupUsers] = useState([]);
  const [dealer, setDealer] = useState<ReactSelectOption | null>(null)

  const dealerOptions = useMemo(
    () =>
      (userReferencePartnerList || []).map((value) => ({
        label: value,
        value: value,
      })),
    [userReferencePartnerList]
  );

  const marketplaceChannel = opportunityFields?.find(field => field?.name === 'Canale__c')?.picklistValues?.find(val => val?.value?.toLowerCase() === 'marketplace');
  const defaultChannel = userRole === RoleEnum.SALES_DEALER ? marketplaceChannel : undefined;
  const hmpBridgeLeadSource = opportunityFields?.find(field => field?.name === 'LeadSource')?.picklistValues?.find(val => val?.value?.toLowerCase() === 'hmp bridge') || {
    "label": "HMP Bridge",
    "value": "HMP Bridge"
  };
  const validationSchema = getOpportunityCreationValidationSchema(userRole);

  const initialValues = useMemo(() => ({
    name: leadWizardData.opportunity?.name || '',
    Canale__c: leadWizardData.opportunity?.Canale__c || defaultChannel,
    Tipologia__c: leadWizardData.opportunity?.Tipologia__c || '',
    Noleggiatore__c: leadWizardData.opportunity?.Noleggiatore__c || '',
    driveItEasy: leadWizardData.opportunity?.driveItEasy || false,
    commissions: leadWizardData.opportunity?.commissions ?? '',
    driverEmail: leadWizardData.opportunity?.driverEmail || '',
    notes: leadWizardData.opportunity?.notes || activeLead?.Messaggio__c || '',
    nomePartner: leadWizardData.opportunity?.nomePartner || leadWizardData.account?.reference_partner || '',
    Offerta_Edison__c: leadWizardData.opportunity?.Offerta_Edison__c,
    salesUser: leadWizardData.opportunity?.salesUser || '',
    referenceUserId: leadWizardData.opportunity?.referenceUserId || '',
    backupUsers: leadWizardData.opportunity?.backupUsers || [],
    provvigioniPassive: leadWizardData.opportunity?.provvigioniPassive || '',
    provvigioniSegnalatore: leadWizardData.opportunity?.provvigioniSegnalatore || '',
    segnalatore: leadWizardData.opportunity?.segnalatore || '',
    ragioneSocialeSegnalatore: leadWizardData.opportunity?.ragioneSocialeSegnalatore || '',
  }), [leadWizardData])

  useEffect(() => {
    if (isBam) return;
    handleApiResponse(apiClient.salesforceAccountController.getSalesUsers())
      .then((res) => {
        setSalesUsers(res);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, [isBam]);

  useEffect(() => {
    handleApiResponse(apiClient.salesforceAccountController.getReferenceUsers())
      .then((res) => {
        const items: RemoteSelectOption[] = res.map((item) =>
          _.isString(item)
            ? {
              value: item,
              label: item,
            }
            : item
        );
        setReferenceUsers(items);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, []);

  useEffect(() => {
    handleApiResponse(apiClient.salesforceAccountController.getBackupUsers())
      .then((res) => {
        setBackupUsers(res);
      })
      .catch((err) => {
        console.error('Error fetching sales users:', err);
      });
  }, []);

  const populateDealer = useCallback(() => {
    if (dealer) return;

    const dealerName =
      leadWizardData.opportunity?.nomePartner ||
      leadWizardData.account?.reference_partner;
    if (!dealerName) return;

    const foundDealer = dealerOptions.find((item) => {
      if (isString(dealerName)) {
        return (
          (dealerName as string).toLowerCase() === item.value?.toLowerCase()
        );
      }
      if (isObject(dealerName)) {
        return (
          (dealerName as ReactSelectOption).value?.toLowerCase() ===
          item.value?.toLowerCase()
        );
      }
    });
    setDealer(foundDealer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, dealerOptions]);

  useEffect(() => populateDealer(), [populateDealer]);

  const isButtonDisabled = (userRole) => {
    return ![
      RoleEnum.SALES,
      RoleEnum.SALES_DEALER,
      RoleEnum.SALES_INTERNAL,
      RoleEnum.MASTER,
      RoleEnum.BACKOFFICE,
      RoleEnum.AREA_MANAGER,
    ].includes(userRole);
  };

  const updateWizardState = useCallback(
    (values: any) => {
      setLeadWizardData({
        ...leadWizardData,
        opportunity: {
          ...leadWizardData.opportunity,
          ...values,
        },
      });
    },
    [leadWizardData, setLeadWizardData]
  );

  const handleFormSubmit = async (values: any) => {
    const { name } = values;

    if (!name) {
      toast.warn('opportunity_name_required');
      return;
    }

    updateWizardState(values);
    setWizardActiveStep(LeadsWizardActiveStepEnum.VEHICLE);
  };

  const accountName = useMemo(() => {
    return (
      'HMP-' +
      (leadWizardData.account
        ? `${leadWizardData?.account.Name || leadWizardData?.account.FirstName
        }-`
        : '')
    );
  }, [leadWizardData.account]);

  const defaultReferenceUser = useMemo(() => {
    const firstValidOption = (referenceUsers || []).find(
      (option) => !option.disabled
    );

    if ([RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole)) {
      return firstValidOption?.value;
    }

    if (
      ![
        RoleEnum.SALES,
        RoleEnum.SALES_DEALER,
        RoleEnum.SALES_INTERNAL,
      ].includes(userRole)
    )
      return;

    const currentUserOption = (referenceUsers || []).find(
      (option) => option.value === user.username && !option.disabled
    );

    return userIsSalesforceSales && currentUserOption
      ? currentUserOption?.value
      : firstValidOption?.value;
  }, [referenceUsers, userRole, user, userIsSalesforceSales]);

  const isAnySale = [
    RoleEnum.SALES,
    RoleEnum.SALES_DEALER,
    RoleEnum.SALES_INTERNAL,
  ].includes(userRole);

  return (
    <Formik
      initialValues={{
        ...initialValues,
        salesUser: initialValues?.salesUser || (isAnySale ? salesUsers?.[0] : undefined),
        referenceUserId: initialValues?.referenceUserId || defaultReferenceUser,
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
    >
      {({ errors, values, handleChange, handleSubmit, setFieldValue, touched, setValues, setFieldTouched }) => {
        const selectedDealer = values.nomePartner && typeof values.nomePartner === 'object' ? values.nomePartner.value : values.nomePartner;

        const isSegnalazione = values.Canale__c?.value?.toLowerCase() === 'segnalatore';
        const filteredSalesUsers: UserDTO[] = selectedDealer
          ? allSalesUsersDTO.filter((u: UserDTO) => u.reference_partner?.includes(selectedDealer))
          : allSalesUsersDTO;
        const selectedSegnalatorePerc = isSegnalazione
          ? allSegnalatori.find((u: UserDTO) => u.username === values.segnalatore)?.provvigioni_segnalatore
          : undefined;
        const filteredSegnalatori: UserDTO[] = selectedDealer
          ? allSegnalatori.filter((u: UserDTO) => u.reference_partner?.includes(selectedDealer))
          : allSegnalatori;
        return (
          <Form onSubmit={handleSubmit}>
            <ForceCommissionsZeroOnSegnalazione
              isSegnalazione={isSegnalazione}
              isMasterOrBackoffice={isMasterOrBackoffice}
              setFieldValue={setFieldValue}
            />
            <ForceLeadSourceOnSegnalazione
              isSegnalazione={isSegnalazione}
              hmpBridgeLeadSource={hmpBridgeLeadSource}
              setFieldValue={setFieldValue}
            />
            <Row className='mt-4 mb-3 align-items-end'>
              <Col xs={3}>
                <Form.Group>
                  <RequiredFormLabel label={i18next.t('name')} />
                  <Form.Control type="text"
                    disabled
                    value={accountName}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Control type="text"
                    name='name'
                    required
                    placeholder={i18next.t('input_name')}
                    value={values.name}
                    onChange={handleChange}
                  />
                  <ValidationError {...{ errors, touched, name: "name" }} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {commissionsLabel}
                    <span className="red-ast">*</span>
                    <small id="info-commissions-euro" className='text-muted ms-2'>
                      <FaInfoCircle size={18} />
                      <ToolTip
                        anchorId="info-commissions-euro"
                        content={t('info_commissions_euro')}
                        className="default"
                      />
                    </small>
                  </Form.Label>
                  <Form.Control type="text"
                    name='commissions'
                    required={!isMasterOrBackoffice}
                    disabled={isMasterOrBackoffice && isSegnalazione}
                    value={values.commissions}
                    onChange={({ target }) => {
                      const next = renderFloatAsStr(target?.value);
                      setFieldValue(target.name, next);
                      if (isSegnalazione) {
                        applySegnalatoreCrossUpdate({
                          field: 'commissions',
                          value: next,
                          isMasterOrBackoffice,
                          segnalatorePerc: selectedSegnalatorePerc,
                          setFieldValue,
                        });
                      }
                    }}
                  />
                  <ValidationError {...{ errors, touched, name: "commissions" }} />
                </Form.Group>
              </Col>
              {isMasterOrBackoffice && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('provvigioni_dealer_euro')}
                      <span className="red-ast">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="provvigioniPassive"
                      value={values.provvigioniPassive || ''}
                      onChange={({ target }) => {
                        const next = renderFloatAsStr(target.value);
                        setFieldValue(target.name, next);
                        if (isSegnalazione) {
                          applySegnalatoreCrossUpdate({
                            field: 'provvigioniPassive',
                            value: next,
                            isMasterOrBackoffice,
                            segnalatorePerc: selectedSegnalatorePerc,
                            setFieldValue,
                          });
                        }
                      }}
                    />
                    <ValidationError {...{ errors, touched, name: "provvigioniPassive" }} />
                  </Form.Group>
                </Col>
              )}
              {opportunityFields?.map((el, idx) => {
                return (
                  <Col md={6} key={el.name}>
                    <Form.Group className="mb-3">
                      <RequiredFormLabel label={el.label} required={['nomePartner', 'Offerta_Edison__c'].includes(el?.name) ? false : undefined} />
                      <Select
                        menuPosition="fixed"
                        isClearable={['nomePartner', 'Offerta_Edison__c'].includes(el?.name) ? true : undefined}
                        name={el.name}
                        placeholder={`${t('select')} ${el.label.toLowerCase()}`}
                        options={el.picklistValues}
                        value={el.name === 'nomePartner' ? dealer : values[el.name]}
                        isDisabled={el.name === 'LeadSource' && isSegnalazione}
                        onChange={(opt: any) => {
                          if (el?.name === 'nomePartner') {
                            setFieldValue('segnalatore', '');
                            setFieldTouched('segnalatore', true, true);
                            setFieldValue('salesUser', '');
                            setFieldTouched('salesUser', true, true);
                            setDealer(opt)
                          }

                          if (
                            el?.name === 'Canale__c'
                            && isSegnalazione
                            && opt?.value?.toLowerCase() !== 'segnalatore'
                          ) {
                            setFieldValue('commissions', '');
                            setFieldValue('provvigioniPassive', '');
                            setFieldValue('provvigioniSegnalatore', '');
                            setFieldValue('segnalatore', '');
                            setFieldValue('LeadSource', null);
                          }
                          setFieldValue(el.name, opt)
                        }}
                      />
                      <ValidationError {...{ errors, touched, name: el.name }} />
                    </Form.Group>
                  </Col>
                )
              })}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={i18next.t('sales_user')} name='salesUser' />
                  <Form.Select
                    aria-label="Sales User"
                    name='salesUser'
                    value={values.salesUser}
                    onChange={handleChange}
                  >
                    <option value="">{t('select_sales_user')}</option>
                    {isBam
                      ? filteredSalesUsers.map((u: UserDTO) => (
                        <option key={u.username} value={u.username}>{u.username}</option>
                      ))
                      : salesUsers && salesUsers.map((user: any) => (
                        <option key={user} value={user}>{user}</option>
                      ))
                    }
                  </Form.Select>
                  <ValidationError {...{ errors, touched, name: "salesUser" }} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={i18next.t('reference_user')} />
                  <Form.Select
                    aria-label="Reference User"
                    name='referenceUserId'
                    value={values.referenceUserId}
                    onChange={handleChange}
                  >
                    <option value=''>{i18next.t('select_reference_user')}</option>
                    {referenceUsers && referenceUsers.map((user: any) => (
                      <option key={user.value} value={user.value} disabled={user.disabled}>
                        {user.label} {user.disabled ? `(${user.disabledReason})` : null}
                      </option>
                    ))}
                  </Form.Select>
                  <ValidationError {...{ errors, touched, name: "referenceUserId" }} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <RequiredFormLabel
                    required={false}
                    label={
                      <>
                        {i18next.t('backup_users')}
                        <small id="info-backup-users" className='text-muted ms-2'>
                          <FaInfoCircle size={18} />
                          <ToolTip
                            anchorId="info-backup-users"
                            content={t('opportunity_backup_users_mail_info')}
                            className="default"
                          />
                        </small>
                      </>
                    } />
                  <Select
                    menuPosition="fixed"
                    aria-label="Backup Users"
                    name='backupUsers'
                    noOptionsMessage={() => t('no_options')}
                    value={values.backupUsers ? values.backupUsers.map((user: string) => ({ label: user, value: user })) : []}
                    onChange={
                      (opts: any) => setFieldValue('backupUsers', opts ? opts.map((opt: any) => opt.value) : [])
                    }
                    options={backupUsers && backupUsers.map((user) => ({ label: user, value: user }))}
                    placeholder={t('select_backup_users')}
                    isMulti
                  />
                  <ValidationError {...{ errors, touched, name: "backupUsers" }} />
                </Form.Group>
              </Col>
            </Row>

            {isBam && isSegnalazione && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel label={t('provvigioni_segnalatore')} name="provvigioniSegnalatore" />
                    <Form.Control
                      type="text"
                      name="provvigioniSegnalatore"
                      value={values.provvigioniSegnalatore || ''}
                      onChange={({ target }) => {
                        const next = renderFloatAsStr(target.value);
                        setFieldValue(target.name, next);
                        applySegnalatoreCrossUpdate({
                          field: 'provvigioniSegnalatore',
                          value: next,
                          isMasterOrBackoffice,
                          segnalatorePerc: selectedSegnalatorePerc,
                          setFieldValue,
                        });
                      }}
                    />
                    <ValidationError {...{ errors, touched, name: "provvigioniSegnalatore" }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel label={'Segnalatore'} name="segnalatore" />
                    <Form.Select
                      name="segnalatore"
                      value={values.segnalatore || ''}
                      onChange={(e) => {
                        const selected = allSegnalatori.find((u: UserDTO) => u.username === e.target.value);
                        setValues({
                          ...values,
                          segnalatore: e.target.value,
                          ragioneSocialeSegnalatore: selected?.business_name || '',
                          commissions: isMasterOrBackoffice ? values.commissions : '',
                          provvigioniPassive: isMasterOrBackoffice ? '' : values.provvigioniPassive,
                          provvigioniSegnalatore: '',
                        }, true);
                      }}
                    >
                      <option value="">{t('select') + ' Segnalatore'}</option>
                      {filteredSegnalatori.map((u: UserDTO) => (
                        <option key={u.username} value={u.username}>
                          {u.username}
                        </option>
                      ))}
                    </Form.Select>
                    <ValidationError {...{ errors, touched, name: "segnalatore" }} />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <TiassistoElement>
              <Row className='align-items-end'>
                {/* DRIVE IT EASY SWITCH */}
                <Col md={6}>
                  <Form.Group className="mb-3 d-flex">
                    <Form.Label
                      className="me-3"
                      htmlFor="driveItEasyAnchor"
                    >
                      {t("drive_it_easy")}
                    </Form.Label>
                    <Form.Check
                      id="driveItEasyAnchor"
                      name="driveItEasy"
                      type="switch"
                      onChange={handleChange}
                      checked={values.driveItEasy}
                      className="hor-form-check-switch"
                      disabled={isButtonDisabled(userRole)}
                    />
                  </Form.Group>
                </Col>

                {/* INPUT DRIVER EMAIL */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('driver_mail')}
                      required={values.driveItEasy}
                      errors={errors} name='driverEmail' />
                    <Form.Control
                      type='email'
                      id="driverEmailAnchor"
                      name="driverEmail"
                      value={values.driverEmail}
                      onChange={handleChange}
                    />
                    <ValidationError {...{ errors, touched, name: "driverEmail" }} />
                  </Form.Group>
                </Col>
              </Row >
            </TiassistoElement>

            <Row>
              <Col md={12}>
                <FormControl
                  as="textarea"
                  placeholder={`${i18next.t('add_notes')}...`}
                  name='notes'
                  onChange={handleChange}
                  style={{ height: '200px' }}
                  value={values.notes}
                />
              </Col>
            </Row>
            <Col className="d-flex justify-content-between align-items-center">
              <Button
                onClick={() => {
                  updateWizardState(values);
                  setWizardActiveStep(LeadsWizardActiveStepEnum.ACCOUNT)
                }}
                style={{ marginTop: '10px', marginRight: '10px' }}
              >
                <FaArrowLeftLong className="me-2" />
                {t('account')}
              </Button>
              <Button
                type="submit"
                style={{ marginTop: '10px' }}
              >
                {t('vehicle')}
                <FaArrowRightLong className="ms-2" />
              </Button>
            </Col>
          </Form>
        )
      }}
    </Formik>
  )
};

export default SfOpportunityForm;
